// pages/AssignManagerCredits.tsx — FINAL RTP INTEGRATED VERSION
// Real-time sync, RTP warning, optimistic UI, toast feedback

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import useAdmin from '../hooks/useAdmin';
import { toast } from 'react-hot-toast';

interface FormData {
  login_id: string;  // FIX: required not optional — .trim() is now safe
  amount: number;
}

export default function AssignManagerCredits() {
  const [loading, setLoading] = useState(false);
  const { token, refreshCsrf, error, setError, socket } = useAdmin();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    defaultValues: { login_id: '', amount: 0 },
  });

  useEffect(() => {
    if (token) refreshCsrf();
  }, [token, refreshCsrf]);

  // Real-time credits update listener (manager credits change pe notify)
  useEffect(() => {
    if (!socket) return;

    const handleCreditsUpdated = (data: {
      managerId?: string;
      credits: number;
      message?: string;
    }) => {
      console.log('Real-time manager credits update:', data);

      if (data.managerId) {
        toast.success(
          data.message || `Manager credits updated! New balance: ${data.credits}`
        );
      } else {
        toast.success(data.message || 'Credits updated somewhere in the system');
      }
    };

    socket.on('credits-updated', handleCreditsUpdated);

    return () => {
      socket.off('credits-updated', handleCreditsUpdated);
    };
  }, [socket]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError('');

    // RTP Warning: Manager credits change house exposure affect karta hai
    if (data.amount !== 0) {
      if (!window.confirm(
        `Assigning ${Math.abs(data.amount)} credits to manager will affect RTP exposure and house balance. Confirm?`
      )) {
        setLoading(false);
        return;
      }
    }

    try {
      const csrfToken = await refreshCsrf();
      const sanitizedData = {
        login_id: DOMPurify.sanitize(data.login_id.trim()),
        amount: Number(data.amount),
      };

      const res = await fetch('/admin/assign-credits-manager', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || localStorage.getItem('adminToken')}`,
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: JSON.stringify(sanitizedData),
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.error?.message || 'Failed to assign manager credits');
      }

      toast.success(`Credits assigned! ${data.amount > 0 ? 'Added' : 'Removed'} ${Math.abs(data.amount)} credits to manager.`);
      reset({ login_id: '', amount: 0 });
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">Assign Credits to Manager</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">
            ✕
          </button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manager Login ID
            </label>
            <input
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g. MGR123456"
              {...register('login_id', {
                required: 'Login ID is required',
              })}
            />
            {errors.login_id && <p className="mt-1 text-sm text-red-600">{errors.login_id.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount
            </label>
            <input
              type="number"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Positive = Add, Negative = Remove"
              {...register('amount', {
                required: 'Amount is required',
                valueAsNumber: true,
              })}
            />
            {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 font-medium rounded-lg text-white transition ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Assigning...
              </span>
            ) : (
              'Assign Credits'
            )}
          </button>
        </form>
      </div>

      {/* RTP Note */}
      <p className="mt-6 text-sm text-gray-500 text-center">
        Manager credits directly impact house RTP exposure and available funds. Use carefully.
      </p>
    </div>
  );
}